import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Lock, User, Eye, EyeOff } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import Logo from '../components/Logo';
import { toast } from '../hooks/use-toast';
import { Toaster } from '../components/ui/toaster';
import { adminLogin } from '../lib/api';

export default function AdminLogin() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [pass, setPass] = useState('');
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !pass) return toast({ title: 'Please enter email and password' });
    setBusy(true);
    try {
      const res = await adminLogin(email, pass);
      localStorage.setItem('aqc_admin_token', res.token);
      localStorage.setItem('aqc_admin_email', res.user?.email || email);
      toast({ title: 'Welcome back', description: 'Signed in to admin dashboard.' });
      navigate('/admin');
    } catch (err) {
      toast({
        title: 'Login failed',
        description: err?.response?.data?.detail || 'Check your credentials and try again.',
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-aqc-cream font-sans-body flex items-center justify-center px-4 relative overflow-hidden">
      <div className="absolute inset-0 bg-navy-grid opacity-95" />
      <div className="relative w-full max-w-md bg-white rounded-2xl shadow-2xl border border-aqc-line p-8">
        <div className="text-center">
          <Logo showWord={false} size={56} />
          <h1 className="mt-4 font-serif text-2xl text-aqc-navy">Admin Sign In</h1>
          <p className="mt-1 text-sm text-aqc-muted">Manage website content, resources & submissions.</p>
        </div>
        <form onSubmit={submit} className="mt-7 space-y-4">
          <div>
            <Label htmlFor="email">Email</Label>
            <div className="relative mt-1.5">
              <User size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-aqc-muted" />
              <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="pl-9" />
            </div>
          </div>
          <div>
            <Label htmlFor="pass">Password</Label>
            <div className="relative mt-1.5">
              <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-aqc-muted" />
              <Input id="pass" type={show ? 'text' : 'password'} value={pass} onChange={(e) => setPass(e.target.value)} placeholder="••••••••" className="pl-9 pr-10" />
              <button type="button" onClick={() => setShow((s) => !s)} className="absolute right-3 top-1/2 -translate-y-1/2 text-aqc-muted">
                {show ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>
          <Button type="submit" disabled={busy} className="w-full bg-aqc-navy hover:bg-[hsl(var(--aqc-navy-2))] text-white h-11">
            {busy ? 'Signing in…' : 'Sign In'}
          </Button>
        </form>
        <p className="mt-6 text-center text-xs text-aqc-muted">
          Signed in credentials are managed by admin. Default password can be changed after first login.
        </p>
      </div>
      <Toaster />
    </div>
  );
}
